import React, { Suspense } from "react";
import { Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import Success from "./components/Success";
import Cancel from "./components/Cancel";
import Reports from "./components/Reports";
import EventReport from "./components/Reports/EventReport";
import AssistanceReport from "./components/Reports/AssistanceReport";
import FoodListReport from "./components/Reports/FoodListReport";
import TraditionalReport from "./components/Reports/TraditionalReport";
import DrinksReport from "./components/Reports/DrinksReport";
import SongsReport from "./components/Reports/SongsReport";
import UnableToAttendReport from "./components/Reports/UnableToAttendReport";
import FoodAndDrinkReport from "./components/Reports/FoodAndDrinkReport";
import WeddingVenueStayReport from "./components/Reports/WeddingVenueStayReport";
import ExternalHotelStayReport from "./components/Reports/ExternalHotelStayReport";
import HotelExtensionDatesReport from "./components/Reports/HotelExtensionDatesReport";
import GuestOfReport from "./components/Reports/GuestOfReport";
import CustomReport from "./components/Reports/CustomReport";
import FlightsReport from "./components/Reports/FlightsReport";
import Mehndi from "./components/Reports/MehndiReport";
import Allergy from "./components/Reports/AllergyReport";
import Expected from "./components/Reports/ExpectedReport";
import PackagesDetails from "./components/Admin/PackagesDetails";
import Invoices from "./components/Admin/Invoices";
import Questions from "./components/Admin/Questions";
import UserDetails from "./components/Admin/UserDetails";
import Admin from "./components/Admin/Admin";
import QuestionSelection from "./components/QuestionSelection";
import NewRsvpForm from "./components/NewRsvpForm";
import RsvpDone from "./components/RsvpDone";
import Gift from "./components/Gift";
import EventsDetails from "./components/EventsDetails.jsx";
import WeddingWebsite from "./components/WeddingWebsite/components/Main.jsx";
import Profile from "./components/Profile.jsx";
import { ErrorBoundary } from 'react-error-boundary';
import { logError } from './redux/Api';
import { createTheme, ThemeProvider } from '@mui/material/styles';

//PAGES
const Home = React.lazy(() => import("./Pages/Home"));

const Login = React.lazy(() => import("./Pages/Login"));
const Signup = React.lazy(() => import("./Pages/Signup"));
const ForgotPass = React.lazy(() => import("./Pages/Forgotpass"));
const AdminReport = React.lazy(() => import("./components/Admin/Reports"));
const AdminUserReports = React.lazy(() => import("./components/Admin/UserReports"));
const AdminUserQuestions = React.lazy(() => import("./components/Admin/UserQuestions"));
const Dashboard = React.lazy(() => import("./components/Dashboard"));
const Events = React.lazy(() => import("./components/Events"));
const Packages = React.lazy(() => import("./components/Packages"));
const EventsList = React.lazy(() => import("./components/EventsList"));
const Template = React.lazy(() => import("./components/Template"));
const FoodQuestion = React.lazy(() => import("./components/FoodQuestion"));
const DrinkQuestion = React.lazy(() => import("./components/DrinkQuestion"));
const RSVPStyle = React.lazy(() => import("./components/RSVPStyle"));
const ResetPassword = React.lazy(() => import("./Pages/ResetPassword"));
const Contacts = React.lazy(() => import("./components/Contacts"));
const PurchaseUpgrade = React.lazy(() => import("./components/PurchaseUpgrade"));
const BuildPDF = React.lazy(() => import("./components/BuildPDF"));
const WWEdit = React.lazy(() => import("./components/WeddingWebsite/components/WWEdit"));


//const InvitationType = React.lazy(() => import("./components/InvitationType"));
const Schedulers = React.lazy(() => import("./components/Schedulers"));
const Invitations = React.lazy(() => import("./components/Invitations"));
const RSVPReview = React.lazy(() => import("./components/RSVPReview"));
const AdminPanel = React.lazy(() => import("./components/Admin/Home"));
const EngagedRsvp = React.lazy(() => import("./components/EngagedRsvp"));
const DateRsvp = React.lazy(() => import("./components/DateRsvp"));

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse"> </div>{" "}
  </div>
);

function App() {
  const isAuthenticated = useSelector((state) => state.auth.user);
  const ProtectedRoute = ({ children }) => {
    return isAuthenticated ? children : <Navigate to="/login" />;
  };

  const theme = createTheme({
    typography: {
      fontFamily: '"Lato", sans-serif'
    },
  });

  const handleError = async (error, info) => {
    try {
      const data={
        errorMessage: error.message,
        errorName: error.name,
        errorStack: error.stack,
        errorString: error.toString(),
        windowLocation: window.location,
        info: info,
        userID: localStorage.getItem("user"),
        ceremonyID: localStorage.getItem("ceremony_id"),
        eventID: localStorage.getItem("eventId"),
      };
      for (const properyName of Object.getOwnPropertyNames(error)) {
        data["raw"+properyName] = error[properyName];
      }
      console.log("handleError", data);
      await logError({error: data});
    } catch (e) {
      console.log("logError", e);
    }
  }
  const ErrorFallback = () => {
    return <><div style={{textAlign:"center",margin:"30px 10px",lineHeight:"2"}}>Sorry, something went wrong. The problem has been logged.<br></br><a href="/dashboard">Continue</a></div></>
  }

  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      <ThemeProvider theme={theme}>
      <Suspense fallback={loading}>
        <Routes>
          <Route exact path="/login" name="Login Page" element={<Login />} />{" "}
          <Route exact path="/signup" name="Signup Page" element={<Signup />} />{" "}
          <Route
            exact
            path="/forgotPassword"
            name="Forgot Password Page"
            element={<ForgotPass />}
          />{" "}
          <Route
            exact
            path="/user/resetPassword/:token"
            name="Forgot Password Page"
            element={<ResetPassword />}
          />{" "}
          <Route
            exact
            path="/rsvp/review"
            name="RSVP Review"
            element={<RSVPReview />}
          />{" "}
          <Route
            exact
            path="/rsvp/code/:rsvpCode" // was: /:user_id/:group_name/:event_id/:id
            name="MyInvPage2"
            element={<NewRsvpForm />}
          />{" "}
          <Route
            exact
            path="/rsvp/done/code/:rsvpCode"
            name="MyInvPage2"
            element={<RsvpDone />}
          />{" "}
          <Route
            exact
            path="/wedding_website/code/:rsvpCode"
            name="Wedding website "
            element={<WeddingWebsite />}
          />{" "}
          <Route
            exact
            path="/wedding_website/edit"
            name="Wedding website edit"
            element={<WWEdit />}
          />{" "}
          <Route
            exact
            path="/engaged/code/:rsvpCode"
            name="Engaged RSVP"
            element={<EngagedRsvp />}
          />{" "}
          <Route
            exact
            path="/date/code/:rsvpCode"
            name="Save the Date RSVP"
            element={<DateRsvp />}
          />{" "}
          {/* PROTECTED ROUTES STARTS FROM HERE */}
          <Route
            exact
            path="/"
            name="Home Page"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/dashboard"
            name="Admin Page"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/profile"
            name="Profile Page"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/myEvents"
            name="Website Page"
            element={
              <ProtectedRoute>
                <Events />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/packages/:eventName"
            name="RSPV packages Page"
            element={
              <ProtectedRoute>
                <Packages />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/eventList"
            name="Event List Page"
            element={
              <ProtectedRoute>
                <EventsList />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/template"
            name="Template Page"
            element={
              <ProtectedRoute>
                <Template />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/contacts"
            name="Contacts Page"
            element={
              <ProtectedRoute>
                <Contacts />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/invitation/schedulers"
            name="MyInvPage2"
            element={
              <ProtectedRoute>
                <Schedulers />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/invitations"
            name="Set guest invitations"
            element={
              <ProtectedRoute>
                <Invitations />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/payment/success"
            name="Payment Success"
            element={
              <ProtectedRoute>
                <Success />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/payment/cancel"
            name="Payment Cancel"
            element={
              <ProtectedRoute>
                <Cancel />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/questions"
            name="Rsvp questions"
            element={
              <ProtectedRoute>
                <QuestionSelection />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/food/questions"
            name="Rsvp food questions"
            element={
              <ProtectedRoute>
                <FoodQuestion />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/drink/questions"
            name="Rsvp drink questions"
            element={
              <ProtectedRoute>
                <DrinkQuestion />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/rsvp/style"
            name="Rsvp style"
            element={
              <ProtectedRoute>
                <RSVPStyle />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guests/template"
            name="Guest Template"
            element={
              <ProtectedRoute>
                <BuildPDF />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/reports"
            name="Guest Reports"
            element={
              <ProtectedRoute>
                <Reports />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/all-events-attendance-report"
            name="Guest Reports"
            element={
              <ProtectedRoute>
                <EventReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/flights-report"
            name="Flights Reports"
            element={
              <ProtectedRoute>
                <FlightsReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/unable-to-attend-report"
            name="UNable to attend report"
            element={
              <ProtectedRoute>
                <UnableToAttendReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/drinks-report"
            name="Drinks Report"
            element={
              <ProtectedRoute>
                <DrinksReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/songs-report"
            name="Song Report"
            element={
              <ProtectedRoute>
                <SongsReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/food-report"
            name="Guest Reports"
            element={
              <ProtectedRoute>
                <FoodListReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/food-and-drink-report"
            name="Food and drink reports"
            element={
              <ProtectedRoute>
                <FoodAndDrinkReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/assistance-report"
            name="Assistance Reports"
            element={
              <ProtectedRoute>
                <AssistanceReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/wedding-venue-stay-report"
            name="Wedding venue stay report"
            element={
              <ProtectedRoute>
                <WeddingVenueStayReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/external-hotel-stay-report"
            name="External hotel stay report"
            element={
              <ProtectedRoute>
                <ExternalHotelStayReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/hotel-extension-dates-report"
            name="Hotel extension dates report"
            element={
              <ProtectedRoute>
                <HotelExtensionDatesReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/mehndi-report"
            name="Guest Reports"
            element={
              <ProtectedRoute>
                <Mehndi />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/allergy-report"
            name="Allergy Report"
            element={
              <ProtectedRoute>
                <Allergy />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/expected-report"
            name="Expected Report"
            element={
              <ProtectedRoute>
                <Expected />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/traditional-styling-report"
            name="Guest Reports"
            element={
              <ProtectedRoute>
                <TraditionalReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/guest-of-report"
            name="Guest of Report"
            element={
              <ProtectedRoute>
                <GuestOfReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/guest/custom-report/:reportIndex"
            name="Custom Report"
            element={
              <ProtectedRoute>
                <CustomReport />
              </ProtectedRoute>
            }
          />{" "}
          <Route
            exact
            path="/package/purchase"
            name="Purchase"
            element={
              <ProtectedRoute>
                <PurchaseUpgrade />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/guest/gifts"
            name="Gifts"
            element={
              <ProtectedRoute>
                <Gift />
              </ProtectedRoute>
            }
          />
          <Route
            exact
            path="/event/details"
            name="Event Details"
            element={
              <ProtectedRoute>
                <EventsDetails />
              </ProtectedRoute>
            }
          />
          {/* NESTED ROUTE FOR ADMIN */}
          <Route
            exact
            path="/admin"
            name="Admin Panel Page"
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            }
          >
            <Route
              exact
              path=""
              name="Admin panel Reports"
              element={
                <ProtectedRoute>
                  <UserDetails />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="admin"
              name="Admin panel admin"
              element={
                <ProtectedRoute>
                  <Admin />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="user/:user_id/questions"
              name="Admin panel user questions"
              element={
                <ProtectedRoute>
                  <AdminUserQuestions />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="user/:user_id/reports"
              name="Admin panel user reports"
              element={
                <ProtectedRoute>
                  <AdminUserReports />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="package-details"
              name="Admin panel Reports"
              element={
                <ProtectedRoute>
                  <PackagesDetails />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="invoice"
              name="Admin panel Reports"
              element={
                <ProtectedRoute>
                  <Invoices />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="faq"
              name="Admin panel Reports"
              element={
                <ProtectedRoute>
                  <Questions />
                </ProtectedRoute>
              }
            />
            <Route
              exact
              path="reports"
              name="Admin panel Reports"
              element={
                <ProtectedRoute>
                  <AdminReport />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </Suspense>
      </ThemeProvider>
      </ErrorBoundary>
    </>
  );
}

export default App;
